import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { observable } from 'mobx';
import { Icon, Spinner } from "@blueprintjs/core";
import qs from 'qs';

const MAX_PER_GROUP = 10;

const sentence = (text) => text && `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
const joined = (...parts) => parts.filter(Boolean).join(', ');

// Work orders, estimates and invoices come back as the same shape.
const workReportGroup = (title, icon, path) => ({
  title,
  icon,
  name: (item) => item.number,
  meta: (item) => joined(item.client_name, sentence(item.status)),
  href: (item) => `/dashboard/${path}/${item.id}/show`,
});

// How each result type from /search reads in the panel and where it leads,
// in the order the groups are shown.
const GROUPS = {
  ClientDecorator: {
    title: 'Clients',
    name: (item) => item.name,
    meta: (item) => item.email,
    href: (item) => `/dashboard/clients/${item.id}?resource=jobs`,
  },
  UserDecorator: {
    title: 'Team',
    name: (item) => `${item.first_name || ''} ${item.last_name || ''}`.trim(),
    meta: (item) => item.email,
    href: (item) => `/dashboard/users/${item.id}/show`,
  },
  JobDecorator: {
    title: 'Jobs',
    icon: 'wrench',
    name: (item) => item.title,
    meta: (item) => item.event_number && `Job ${item.event_number}`,
    href: (item, { jobSection }) => `/dashboard/jobs/${item.id}/${jobSection}`,
  },
  WorkReport: workReportGroup('Work orders', 'clipboard', 'work-reports'),
  Quote: workReportGroup('Estimates', 'document', 'quotes'),
  Invoice: workReportGroup('Invoices', 'dollar', 'invoices'),
  PurchaseOrder: {
    title: 'Purchase orders',
    icon: 'shopping-cart',
    name: (item) => item.purchase_order_number && `PO ${item.purchase_order_number}`,
    meta: (item) => joined(item.supplier_name, sentence(item.status)),
    href: (item) => `/dashboard/purchase-orders/${item.id}/show-purchase-order`,
  },
  Project: {
    title: 'Projects',
    icon: 'projects',
    name: (item) => item.name,
    meta: (item) => item.number && `Project ${item.number}`,
    href: (item, { viewStore }) =>
      `/dashboard/projects/${item.id}/show?${qs.stringify(viewStore.projectShowSearch, { encode: false })}`,
  },
  Equipment: {
    title: 'Equipment',
    icon: 'cog',
    name: (item) => [item.make, item.eq_model].filter(Boolean).join(' '),
    meta: (item) => item.serial_number && `Serial ${item.serial_number}`,
    href: (item, { viewStore }) =>
      `/dashboard/equipments/${item.id}/show?${qs.stringify(viewStore.equipmentShowSearch, { encode: false })}`,
  },
  Supplier: {
    title: 'Suppliers',
    icon: 'shop',
    name: (item) => item.name,
    meta: (item) => item.email || item.phone,
    href: (item) => `/dashboard/suppliers/${item.id}/show`,
  },
};

const initials = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');

@inject('searchStore', 'viewStore') @observer
class GlobalSearchBar extends Component {

  @observable term = "";
  @observable panelOpen = false;
  @observable activeIndex = -1;

  rootRef = React.createRef();
  inputRef = React.createRef();

  componentDidMount() {
    document.addEventListener('keyup', this.searchListener, false);
    document.addEventListener('mousedown', this.onDocumentMouseDown, false);
  }

  componentWillUnmount() {
    document.removeEventListener('keyup', this.searchListener, false);
    document.removeEventListener('mousedown', this.onDocumentMouseDown, false);
  }

  searchListener = (e) => {
    if (e.ctrlKey && e.key === 's' && this.inputRef.current) {
      this.inputRef.current.focus();
    }
  };

  onDocumentMouseDown = (e) => {
    if (this.rootRef.current && !this.rootRef.current.contains(e.target)) {
      this.panelOpen = false;
    }
  };

  doSearch() {
    if (!this.term.trim()) return;
    this.activeIndex = -1;
    this.panelOpen = true;
    this.props.searchStore.doTermSearch(this.term);
  }

  clear() {
    this.term = "";
    this.activeIndex = -1;
    this.panelOpen = false;
    this.props.searchStore.search.isSearched = false;
    this.props.searchStore.search.isError = false;
    this.props.searchStore.search.objects = {};
    if (this.inputRef.current) this.inputRef.current.focus();
  }

  handleTermChange(event) {
    this.term = event.target.value;
    if (this.term === '') this.clear();
  }

  // Results flattened in display order, for arrow-key movement.
  get results() {
    const objects = this.props.searchStore.search.objects || {};
    const { viewStore } = this.props;
    const jobSection = viewStore.jobTab.section;
    return Object.keys(GROUPS)
      .filter((key) => Array.isArray(objects[key]))
      .map((key) => ({
        key,
        group: GROUPS[key],
        items: objects[key].slice(0, MAX_PER_GROUP).map((item) => ({
          item,
          href: GROUPS[key].href(item, { jobSection, viewStore }),
        })),
      }))
      .filter(({ items }) => items.length > 0);
  }

  onKeyDown(event) {
    const flat = this.results.flatMap(({ items }) => items);
    if (event.key === 'Enter') {
      if (this.panelOpen && this.activeIndex >= 0 && flat[this.activeIndex]) {
        window.location.href = flat[this.activeIndex].href;
      } else {
        this.doSearch();
      }
    } else if (event.key === 'Escape') {
      this.clear();
    } else if (event.key === 'ArrowDown' && flat.length) {
      event.preventDefault();
      this.panelOpen = true;
      this.activeIndex = (this.activeIndex + 1) % flat.length;
    } else if (event.key === 'ArrowUp' && flat.length) {
      event.preventDefault();
      this.activeIndex = this.activeIndex <= 0 ? flat.length - 1 : this.activeIndex - 1;
    }
  }

  renderPanel() {
    const {
      search: { isLoading, isError, isSearched },
    } = this.props.searchStore;
    const term = this.term.trim();
    const groups = this.results;

    let status = null;
    if (isLoading) {
      status = <span className="global-search__status-loading"><Spinner size={14} /> Searching…</span>;
    } else if (isError) {
      status = 'Search didn’t go through. Press Enter to try again.';
    } else if (isSearched && groups.length === 0) {
      status = `Nothing matches “${term}”. Try a name, email, or a job, invoice or PO number.`;
    }

    let index = -1;
    return (
      <div className="global-search__panel" role="listbox" aria-label="Search results">
        {status && <div className="global-search__status">{status}</div>}
        {!isLoading && groups.map(({ key, group, items }) => (
          <section key={key} className="global-search__group">
            <h6 className="global-search__group-title">
              {group.title}
              <span className="global-search__group-count">{items.length}</span>
            </h6>
            <ul>
              {items.map(({ item, href }) => {
                index += 1;
                const name = group.name(item) || 'Untitled';
                const meta = group.meta(item);
                const active = index === this.activeIndex;
                return (
                  <li key={item.id}>
                    <a
                      href={href}
                      role="option"
                      aria-selected={active}
                      className={`global-search__result${active ? ' is-active' : ''}`}
                    >
                      <span className="global-search__badge" aria-hidden="true">
                        {group.icon ? <Icon icon={group.icon} iconSize={14} /> : initials(name)}
                      </span>
                      <span className="global-search__text">
                        <span className="global-search__name">{name}</span>
                        {meta && <span className="global-search__meta">{meta}</span>}
                      </span>
                      {key === 'ClientDecorator' && item.do_not_service && (
                        <span className="global-search__flag">Do not service</span>
                      )}
                    </a>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    );
  }

  render() {
    const { isLoading, isSearched, isError } = this.props.searchStore.search;
    const showPanel = this.panelOpen && (isLoading || isSearched || isError);
    return (
      <div className="global-search" role="search" ref={this.rootRef}>
        <div className="global-search__field">
          <Icon icon="search" iconSize={15} className="global-search__icon" />
          <input
            ref={this.inputRef}
            type="text"
            id="search-field"
            className="global-search__input"
            placeholder="Search clients, jobs, invoices and more"
            aria-label="Search clients, team, jobs, work orders, estimates, invoices, purchase orders, projects, equipment and suppliers"
            autoComplete="off"
            value={this.term}
            onChange={this.handleTermChange.bind(this)}
            onKeyDown={this.onKeyDown.bind(this)}
            onFocus={() => { if (isSearched) this.panelOpen = true; }}
          />
          {this.term ? (
            <button
              type="button"
              className="global-search__clear"
              aria-label="Clear search"
              onClick={this.clear.bind(this)}
            >
              <Icon icon="small-cross" iconSize={16} />
            </button>
          ) : (
            <kbd className="global-search__hint" aria-hidden="true">Ctrl S</kbd>
          )}
        </div>
        {showPanel && this.renderPanel()}
      </div>
    );
  }

};

export default GlobalSearchBar;
